import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  Refresh,
  AssignmentReturn,
  PendingActions,
  CheckCircle,
  ReceiptLong,
  AccountBalanceWallet,
  FilterList,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import {
  salesReturnService,
  SalesReturnRow,
  SalesReturnSummary,
  ReturnStatus,
} from '../../services/salesReturnService';

// ---------------------------------------------------------------------------
// Sales Return list -- returns raised by customers against a delivered
// invoice. Mirrors SalesContractPage.tsx's shape: a stat-card row sourced
// from /sales-returns/summary, a filter bar, and a single results table.
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: ReturnStatus[] = [
  'REQUESTED',
  'APPROVED',
  'RECEIVED',
  'INSPECTED',
  'ACCEPTED',
  'CREDIT_NOTE_ISSUED',
  'CLOSED',
  'REJECTED',
  'CANCELLED',
];

const STATUS_TONE: Record<ReturnStatus, StatusTone> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  RECEIVED: 'info',
  INSPECTED: 'info',
  ACCEPTED: 'success',
  CREDIT_NOTE_ISSUED: 'success',
  CLOSED: 'neutral',
  REJECTED: 'error',
  CANCELLED: 'error',
};

const TYPE_TINT: Record<string, string> = {
  DAMAGED: '#dc2626',
  WRONG_ITEM: '#d97706',
  EXCESS: '#0284c7',
  QUALITY_ISSUE: '#7c3aed',
  CUSTOMER_REJECTION: '#be123c',
  OTHER: '#64748b',
};

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesReturnPage: React.FC = () => {
  const navigate = useNavigate();

  const [returns, setReturns] = useState<SalesReturnRow[]>([]);
  const [summary, setSummary] = useState<SalesReturnSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<ReturnStatus | ''>('');
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      salesReturnService.list({ status: statusFilter || undefined, search: search || undefined }),
      salesReturnService.getSummary().catch(() => ({ summary: null as unknown as SalesReturnSummary })),
    ])
      .then(([listRes, summaryRes]) => {
        setReturns(listRes.returns || []);
        setSummary(summaryRes.summary || null);
      })
      .catch((err: any) => setError(err.message || 'Failed to load sales returns'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const handle = setTimeout(fetchAll, search ? 350 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAll]);

  const hasFilters = !!(statusFilter || search);
  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
  };

  return (
    <Box>
      <PageHeader
        title="Sales Returns"
        subtitle="Customer returns raised against delivered invoices, through approval, inspection and credit note"
        actions={
          <>
            <IconButton onClick={fetchAll} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={() => navigate('/sales/returns/new')}>
              New Return
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard
          title="Total Returns"
          value={loading || !summary ? null : summary.totalCount}
          icon={<AssignmentReturn />}
          color="#2563eb"
        />
        <StatCard
          title="Pending"
          value={loading || !summary ? null : summary.pendingCount}
          icon={<PendingActions />}
          color="#d97706"
        />
        <StatCard
          title="Accepted"
          value={loading || !summary ? null : summary.acceptedCount}
          icon={<CheckCircle />}
          color="#059669"
        />
        <StatCard
          title="Credit Notes Issued"
          value={loading || !summary ? null : summary.creditNoteIssuedCount}
          icon={<ReceiptLong />}
          color="#7c3aed"
        />
        <StatCard
          title="Total Value"
          value={loading || !summary ? null : formatIndianNumber(summary.totalValue)}
          icon={<AccountBalanceWallet />}
          color="#0284c7"
        />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Search"
          placeholder="Return #, invoice # or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ReturnStatus | '')}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Return', 'Date', 'Customer', 'Invoice', 'Type', 'Value', 'Status'].map((h, i) => (
                  <TableCell key={h} align={i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchAll} />
                  </TableCell>
                </TableRow>
              ) : returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState title="No returns match these filters" hint="Clear the filters, or raise a new return." />
                  </TableCell>
                </TableRow>
              ) : (
                returns.map((r) => (
                  <TableRow
                    key={r.id}
                    sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    hover
                    onClick={() => navigate(`/sales/returns/${r.id}`)}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={r.return_number} tint="#0284c7" icon={<AssignmentReturn fontSize="small" />} />
                    </TableCell>
                    <TableCell>{fmtDate(r.return_date)}</TableCell>
                    <TableCell>{r.customer_name || '—'}</TableCell>
                    <TableCell>{r.invoice_number || '—'}</TableCell>
                    <TableCell>
                      <TypePill label={r.return_type.replace(/_/g, ' ')} tint={TYPE_TINT[r.return_type] || '#64748b'} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {formatIndianNumber(toNum(r.total_amount))}
                    </TableCell>
                    <TableCell>
                      <StatusDot label={r.status.replace(/_/g, ' ')} tone={STATUS_TONE[r.status] || 'neutral'} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>
    </Box>
  );
};

export default SalesReturnPage;
